<template>
  <div class="pdf-viewer">
    <!-- 自定义工具栏插槽（完全自定义时使用） -->
    <slot name="toolbar" />

    <!-- 默认工具栏：通过 toolbarButtons 配置按钮 -->
    <div class="pdf-toolbar" v-if="showToolbar && !$slots.toolbar">
      <div class="toolbar-left">
        <!-- 兜底：没配置时显示默认按钮 -->
        <template v-if="!toolbarButtons || !toolbarButtons.length">
          <button @click="firstPage" :disabled="currentPage <= 1" class="toolbar-btn">首页</button>
          <button @click="prevPage" :disabled="currentPage <= 1" class="toolbar-btn">上一页</button>
          <span class="page-info">{{ currentPage }} / {{ pageCount }}</span>
          <button @click="nextPage" :disabled="currentPage >= pageCount" class="toolbar-btn">下一页</button>
          <button @click="lastPage" :disabled="currentPage >= pageCount" class="toolbar-btn">末页</button>
          <template v-if="mode !== 'iframe'">
            <button @click="zoomOut" class="toolbar-btn">-</button>
            <span class="scale-info">{{ Math.round(scale * 100) }}%</span>
            <button @click="zoomIn" class="toolbar-btn">+</button>
            <button @click="toggleRotation" class="toolbar-btn" title="顺时针旋转90度"><i class="el-icon-refresh"></i></button>
            <span class="rotation-info">{{ rotation }}°</span>
          </template>
          <button v-if="showVerifySeal" type="primary" @click="handleVerifySeal" class="toolbar-btn verify-seal-btn" title="验章">验章</button>
          <button @click="downloadPdf" :disabled="!pdfUrl || downloading" class="toolbar-btn" :class="{ 'is-loading': downloading }">
            <span v-if="downloading">下载中...</span>
            <span v-else>下载</span>
          </button>
        </template>

        <!-- 动态渲染配置的按钮 -->
        <template v-else>
          <button v-for="btn in toolbarButtonEls" :key="btn" @click="handleToolbarBtnClick(btn)" :disabled="isBtnDisabled(btn)" :class="getBtnClass(btn)" :title="getBtnTitle(btn)"><span v-html="getBtnText(btn)"></span></button>
          <span v-for="btn in toolbarSpanBtns" :key="btn" :class="getBtnClass(btn)"><span v-html="getBtnText(btn)"></span></span>
          <input v-for="btn in toolbarInputBtns" :key="btn" type="number" v-model="pageInput" @keyup.enter="handlePageJump" class="page-input" min="1" placeholder="页码" />
        </template>
      </div>
    </div>

    <div class="pdf-content" ref="pdfContent">
      <div v-if="mode === 'iframe'" class="pdf-iframe-container">
        <iframe
          v-if="pdfUrl"
          ref="pdfIframe"
          :key="iframeKey"
          :src="`/static/pdfjs-2.4.456-dist/web/viewer.html?file=${encodeURIComponent(pdfUrl)}&locale=zh-CN#disableHistory=true`"
          class="pdf-iframe"
        ></iframe>
        <div v-else class="pdf-placeholder">
          <i class="el-icon-document"></i>
          <p>请选择PDF文件查看</p>
        </div>
      </div>
      <div v-else class="pdf-canvas-container">
        <div v-if="loading" class="pdf-loading">
          <i class="el-icon-loading"></i>
          <p >PDF 加载中...</p>
        </div>
        <div v-else-if="pdfUrl" class="pdf-pages" ref="pdfPages">
          <div v-for="n in allPages" :key="n" class="pdf-page" :data-page="n" :style="pageStyle(n)">
            <div class="pdf-page-container" :style="containerStyle(n)">
              <canvas :ref="'pageCanvas_' + n" class="pdf-document"></canvas>
            </div>
          </div>
        </div>
        <div v-else class="pdf-placeholder">
          <i class="el-icon-document"></i>
          <p>请选择PDF文件查看</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PdfViewer',
  props: {
    pdfUrl: {
      type: String,
      default: ''
    },
    showToolbar: {
      type: Boolean,
      default: true
    },
    downloadFileName: {
      type: String,
      default: 'document.pdf'
    },
    initialScale: {
      type: Number,
      default: 1.0
    },
    showVerifySeal: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'iframe',
      validator: (v) => ['iframe', 'canvas'].includes(v)
    },
    externalScale: {
      type: Number,
      default: null
    },
    externalRotation: {
      type: Number,
      default: null
    },
    /**
     * 工具栏按钮列表，按顺序从左到右排列。
     * 可选值：firstPage(首页)、prevPage(上一页)、nextPage(下一页)、lastPage(末页)、
     * pageInfo(页码信息)、pageJump(页码跳转)、zoomIn(放大)、zoomOut(缩小)、
     * scaleInfo(缩放比例)、rotateCw(顺时针旋转90°)、rotateCcw(逆时针旋转90°)、
     * rotationInfo(旋转角度)、verifySeal(验章)、documentProperties(文档属性)、download(下载)
     * @type {Array<'firstPage'|'prevPage'|'nextPage'|'lastPage'|'pageInfo'|'pageJump'|'zoomIn'|'zoomOut'|'scaleInfo'|'rotateCw'|'rotateCcw'|'rotationInfo'|'verifySeal'|'documentProperties'|'download'>}
     */
    toolbarButtons: {
      type: Array,
      default: () => ['firstPage', 'prevPage', 'nextPage', 'lastPage', 'rotateCw', 'verifySeal', 'documentProperties', 'download']
    }
  },
  data() {
    return {
      loading: false,
      downloading: false,
      pdfDocument: null,
      currentPage: 1,
      pageInput: '1',
      scale: 1.0,
      pdfjsLib: null,
      rotation: 0,
      isRenderingAll: false,
      _scrollTargets: [],
      iframeKey: 0,
      _io: null,
      _renderedPages: new Set(),
      _renderingPages: new Set(),
      _renderQueue: [],
      _activeRenders: 0,
      _renderTasks: {},
      pageCount: 0,
      currentCdnHost: 'https://unpkg.zhimg.com/pdfjs-dist@2.16.105/',
      pageHeight: 800,
      pageGap: 12,
      virtualBuffer: 2,
      jumpRenderBuffer: 1,
      scrollCurrentPage: 1,
      _scrollRafId: null,
      _scrollTimerRafId: null,
      _scrollSettleTimer: null,
      _scrollSettleDelay: 100,
      _isUserScrollingFast: false,
      _scrollAdjustTs: 0,
      _pageObserver: null,
      _pageObserverPollRaf: null,
      pageRenderCompleted: {},
      pageLoadingStates: {},
      _pageViewportHeights: {},
      _firstPageHeight: null,
      _loadingTotalBytes: 0,
      _loadingLoadedBytes: 0,
      _loadingProgress: 0,
      _loadingStartTime: 0,
      _lastProgressUpdate: 0,
      _lastProgressEmitTs: 0,
      _fetchPhase: 'idle',
      _loadingSpeedSample: [],
      _rafId: null,
      _loadingPhaseTimer: null,
      _loadingHint: '',
      _pageCache: {},
      _maxCacheSize: 10,
      _baseMaxCacheSize: 10,
      _baseVirtualBuffer: 2,
      _baseJumpRenderBuffer: 1,
      _fastJumpMode: false,
      _fastJumpTarget: null,
      _fastJumpRestoreTimer: null,
      _fastJumpScaleFactor: 0.75,
      _fastJumpMinScale: 0.5,
      _fastJumpDistanceThreshold: 8,
      _dpiMode: 0,
      _lowDpiScale: 0.4,
      _normalDpiScale: 0.8,
      _highDpiScale: 1.0,
      _pendingUpgradePage: null,
      _upgradeRafId: null,
      _loadSessionId: 0,
      _activeLoadingTask: null,
      _iframeMessageHandler: null,
      _iframeScaleSynced: false,
      _iframeSuppressCmd: false
    }
  },
  computed: {
    visibleToolbarButtons() {
      return (this.toolbarButtons && this.toolbarButtons.length) ? this.toolbarButtons : [];
    },
    allPages() {
      if (!this.pageCount) return [];
      return Array.from({ length: this.pageCount }, (_, i) => i + 1);
    },
    toolbarButtonEls() {
      if (!this.toolbarButtons || !this.toolbarButtons.length) return [];
      return this.toolbarButtons.filter(b => b !== 'documentProperties');
    },
    toolbarSpanBtns() {
      if (!this.toolbarButtons || !this.toolbarButtons.length) return [];
      return this.toolbarButtons.filter(b => ['pageInfo', 'scaleInfo', 'rotationInfo'].includes(b));
    },
    toolbarInputBtns() {
      if (!this.toolbarButtons || !this.toolbarButtons.length) return [];
      return this.toolbarButtons.filter(b => b === 'pageJump');
    },
    loadingProgressText() {
      if (this._loadingHint) {
        return this._loadingHint;
      }
      if (this._loadingProgress >= 0) {
        return `加载中 ${this._loadingProgress}%`;
      }
      if (this._loadingLoadedBytes > 0) {
        return `加载中 ${(this._loadingLoadedBytes / 1024 / 1024).toFixed(1)}MB`;
      }
      return '加载中...';
    }
  },
  watch: {
    pdfUrl: {
      handler(newUrl) {
        if (newUrl) {
          this.iframeKey++
          this.loadPdfFile(newUrl);
        } else {
          this.resetPdf();
        }
      },
      immediate: true
    },
    initialScale: {
      handler(newScale) {
        this.scale = newScale;
        if (this.pdfDocument) {
          this.updatePdfDisplay();
        }
      }
    },
    currentPage(newPage) {
      this.pageInput = String(newPage || 1);
      if (this.mode === 'iframe' && !this._iframeSuppressCmd) {
        this.sendIframeCommand({ action: 'gotoPage', page: newPage });
      }
    },
    externalScale: {
      handler(newScale) {
        if (newScale == null) return;
        const clamped = Math.min(Math.max(newScale, 0.2), 3.0);
        this.scale = clamped;
        if (this.mode === 'iframe') {
          this.sendIframeCommand({ action: 'setScale', scale: this.scale });
        } else if (this.pdfDocument) {
          this.updatePdfDisplay();
        }
        this.$emit('scale-changed', this.scale);
      }
    },
    externalRotation: {
      handler(newRotation) {
        if (newRotation == null) return;
        this.rotation = ((newRotation % 360) + 360) % 360;
        if (this.mode === 'iframe') {
          this.sendIframeCommand({ action: 'setRotation', degrees: this.rotation });
        } else if (this.pdfDocument) {
          this.updatePdfDisplay();
        }
        this.$emit('rotation-changed', this.rotation);
      }
    },
    pageCount(val) {
      if (val > 0 && this.pdfDocument) {
        this.$nextTick(() => {
          this.renderNearby(this.currentPage || 1, this.virtualBuffer);
        });
      }
    }
  },
  mounted() {
    if (!Array.isArray(this._scrollTargets)) {
      this._scrollTargets = []
    }
    const container = this.$refs.pdfContent;
    if (container) {
      container.addEventListener('scroll', this.handleScroll, { passive: true });
      this._scrollTargets.push(container);
    }
    this.bindScrollableAncestors();
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleScroll, { passive: true });

    this._iframeMessageHandler = (e) => {
      if (!e.data || e.data.type !== 'pdfviewer-state') return;
      const { currentPage, pageCount, scale } = e.data;
      this._iframeSuppressCmd = true;
      if (currentPage) this.currentPage = currentPage;
      if (pageCount) this.pageCount = pageCount;
      if (scale && !this._iframeScaleSynced) {
        this.scale = scale;
        this._iframeScaleSynced = true;
      }
      this.$nextTick(() => { this._iframeSuppressCmd = false; });
    };
    window.addEventListener('message', this._iframeMessageHandler);
  },
  methods: {
    sendIframeCommand(cmd) {
      const iframe = this.$refs.pdfIframe;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({ type: 'pdfviewer-cmd', ...cmd }, '*');
      }
    },

    isSessionActive(sessionId) {
      return sessionId && sessionId === this._loadSessionId;
    },

    async destroyPdfDocument(doc) {
      if (!doc || typeof doc.destroy !== 'function') return;
      try {
        await doc.destroy();
      } catch (_) {}
    },

    clearAllCanvases() {
      const canvasRefs = Object.keys(this.$refs || {}).filter(key => key.indexOf('pageCanvas_') === 0);
      canvasRefs.forEach(key => {
        const ref = this.$refs[key];
        const canvases = Array.isArray(ref) ? ref : [ref];
        canvases.forEach(canvas => {
          if (!canvas) return;
          const context = canvas.getContext && canvas.getContext('2d');
          if (context) {
            context.clearRect(0, 0, canvas.width, canvas.height);
          }
          canvas.width = 0;
          canvas.height = 0;
        });
      });
    },

    adjustPerformanceProfile(pageCount) {
      const totalPages = Number(pageCount) || 0;

      this.virtualBuffer = this._baseVirtualBuffer;
      this.jumpRenderBuffer = this._baseJumpRenderBuffer;
      this._maxCacheSize = this._baseMaxCacheSize;

      if (totalPages >= 400) {
        this.virtualBuffer = 1;
        this.jumpRenderBuffer = 0;
        this._maxCacheSize = 4;
      } else if (totalPages >= 200) {
        this.virtualBuffer = 1;
        this.jumpRenderBuffer = 1;
        this._maxCacheSize = 6;
      } else if (totalPages >= 100) {
        this.virtualBuffer = 1;
        this.jumpRenderBuffer = 1;
        this._maxCacheSize = 8;
      }
    },

    startPhaseProgress(startPercent, endPercent, messages, interval = 700) {
      this.stopPhaseProgress();
      const labels = Array.isArray(messages) && messages.length ? messages : ['处理中...'];
      let index = 0;
      let current = startPercent;
      this._loadingHint = labels[0];
      this._emitLoadingProgress(current, this._loadingTotalBytes, labels[0]);

      this._loadingPhaseTimer = setInterval(() => {
        index = (index + 1) % labels.length;
        current = Math.min(current + 1, endPercent);
        this._loadingHint = labels[index];
        this._emitLoadingProgress(current, this._loadingTotalBytes, labels[index]);
        if (current >= endPercent) {
          current = endPercent;
        }
      }, interval);
    },

    stopPhaseProgress() {
      if (this._loadingPhaseTimer) {
        clearInterval(this._loadingPhaseTimer);
        this._loadingPhaseTimer = null;
      }
    },

    getCanvasForPage(pageNumber) {
      const ref = this.$refs['pageCanvas_' + pageNumber];
      if (!ref) return null;
      return Array.isArray(ref) ? ref[0] : ref;
    },

    isToolbarBtn(btn, type) {
      const typeMap = { button: ['prevPage', 'nextPage', 'firstPage', 'lastPage', 'zoomIn', 'zoomOut', 'rotateCw', 'rotateCcw', 'verifySeal', 'download'], span: ['pageInfo', 'scaleInfo', 'rotationInfo'], input: ['pageJump'] };
      return (typeMap[type] || []).includes(btn);
    },

    isBtnDisabled(btn) {
      if (btn === 'prevPage' || btn === 'firstPage') return this.currentPage <= 1;
      if (btn === 'nextPage' || btn === 'lastPage') return this.currentPage >= this.pageCount;
      if (btn === 'download') return !this.pdfUrl || this.downloading;
      return false;
    },

    getBtnClass(btn) {
      if (btn === 'pageInfo') return 'page-info';
      if (btn === 'scaleInfo') return 'scale-info';
      if (btn === 'rotationInfo') return 'rotation-info';
      if (btn === 'verifySeal') return 'toolbar-btn verify-seal-btn';
      if (btn === 'download' && this.downloading) return 'toolbar-btn is-loading';
      return 'toolbar-btn';
    },

    getBtnTitle(btn) {
      const map = { prevPage: '上一页', nextPage: '下一页', firstPage: '首页', lastPage: '末页', zoomIn: '放大', zoomOut: '缩小', rotateCw: '顺时针旋转', verifySeal: '验章', download: '下载' };
      return map[btn] || '';
    },

    getBtnText(btn) {
      if (btn === 'pageInfo') return `${this.currentPage} / ${this.pageCount}`;
      if (btn === 'scaleInfo') return `${Math.round(this.scale * 100)}%`;
      if (btn === 'rotationInfo') return `${this.rotation}°`;
      if (btn === 'prevPage') return '上一页';
      if (btn === 'nextPage') return '下一页';
      if (btn === 'firstPage') return '首页';
      if (btn === 'lastPage') return '末页';
      if (btn === 'zoomIn') return '+';
      if (btn === 'zoomOut') return '-';
      if (btn === 'rotateCw') return '<i class="el-icon-refresh"></i>';
      if (btn === 'verifySeal') return '验章';
      // if (btn === 'documentProperties') return '文档属性';
      if (btn === 'download') return this.downloading ? '下载中...' : '下载';
      return '';
    },

    handleToolbarBtnClick(btn) {
      switch (btn) {
        case 'prevPage': this.prevPage(); break;
        case 'nextPage': this.nextPage(); break;
        case 'firstPage': this.firstPage(); break;
        case 'lastPage': this.lastPage(); break;
        case 'pageJump': this.handlePageJump(); break;
        case 'zoomOut': this.zoomOut(); break;
        case 'zoomIn': this.zoomIn(); break;
        case 'rotateCw': this.rotateCw(); break;
        case 'verifySeal': this.handleVerifySeal(); break;
        // case 'documentProperties': this.openDocumentProperties(); break;
        case 'download': this.downloadPdf(); break;
      }
    },

    bindScrollableAncestors() {
      let el = this.$el && this.$el.parentElement;
      const seen = new Set();
      while (el && el !== document.body && el !== document.documentElement) {
        if (seen.has(el)) break;
        seen.add(el);
        try {
          const style = window.getComputedStyle(el);
          const overflowY = style.overflowY;
          const isScrollable = (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1;
          if (isScrollable) {
            el.addEventListener('scroll', this.handleScroll, { passive: true });
            this._scrollTargets.push(el);
          }
        } catch (e) { }
        el = el.parentElement;
      }
    },
    loadScriptWithFallback(primaryUrl, fallbackUrl) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = primaryUrl;
        script.async = true;
        
        script.onload = () => {
          resolve(false);
        };
        
        script.onerror = () => {
          console.warn(`[PdfViewer] 主 CDN 节点加载失败: ${primaryUrl}，正在尝试备用 CDN 节点...`);
          try {
            document.head.removeChild(script);
          } catch (e) {}
          
          const fallbackScript = document.createElement('script');
          fallbackScript.src = fallbackUrl;
          fallbackScript.async = true;
          
          fallbackScript.onload = () => {
            resolve(true);
          };
          fallbackScript.onerror = () => {
            reject(new Error('所有 PDF.js 资源加载点都不可用'));
          };
          
          document.head.appendChild(fallbackScript);
        };
        
        document.head.appendChild(script);
      });
    },

    async loadPdfJs() {
      if (this.pdfjsLib) {
        return this.pdfjsLib;
      }

      const primaryHost = 'https://unpkg.zhimg.com/pdfjs-dist@2.16.105/';
      const fallbackHost = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/';

      try {
        const isFallbackActive = await this.loadScriptWithFallback(
          primaryHost + 'build/pdf.min.js',
          fallbackHost + 'build/pdf.min.js'
        );

        this.pdfjsLib = window.pdfjsLib;
        this.currentCdnHost = isFallbackActive ? fallbackHost : primaryHost;

        this.pdfjsLib.GlobalWorkerOptions.workerSrc = this.currentCdnHost + 'build/pdf.worker.min.js';
        return this.pdfjsLib;
      } catch (error) {
        console.error('PDF.js加载失败：', error);
        throw error;
      }
    },

    async loadPdfFile(src) {
      const sessionId = Date.now() + Math.random();
      const previousDoc = this.pdfDocument;
      const previousLoadingTask = this._activeLoadingTask;
      this._loadSessionId = sessionId;
      this._activeLoadingTask = null;
      this.pdfDocument = null;
      this.pauseIntersectionObserver();
      if (previousLoadingTask && typeof previousLoadingTask.destroy === 'function') {
        try {
          await previousLoadingTask.destroy();
        } catch (_) {}
      }
      await this.destroyPdfDocument(previousDoc);
      try {
        this.loading = true;
        this.currentPage = 1;
        this.scale = this.initialScale;

        this._loadingProgress = 0;
        this._loadingTotalBytes = 0;
        this._loadingLoadedBytes = 0;
        this._loadingStartTime = Date.now();
        this._lastProgressUpdate = 0;
        this._lastProgressEmitTs = 0;
        this._fetchPhase = 'cdn';
        this._loadingHint = '';
        this.stopPhaseProgress();
        this._loadingSpeedSample = [];
      this._pageCache = {};
      this._pendingUpgradePage = null;
      this._stopFastJump();
      if (this._upgradeRafId) {
        cancelAnimationFrame(this._upgradeRafId);
        this._upgradeRafId = null;
      }

      Object.keys(this._renderTasks || {}).forEach(n => this.cancelRender(Number(n)));
      this._renderTasks = {};
      this._renderQueue = [];
      this._activeRenders = 0;
      this._renderedPages = new Set();
      this._renderingPages = new Set();
      this.pageRenderCompleted = {};
      this.pageLoadingStates = {};
      this._pageViewportHeights = {};
      this._firstPageHeight = null;

        // ---- 阶段 1：加载 PDF.js 库 ----
        this._emitLoadingProgress(0, 0, '正在加载渲染引擎...');
        const pdfjsLib = await this.loadPdfJs();
        if (!pdfjsLib) throw new Error('PDF.js加载失败');
        this._fetchPhase = 'fetching';

        let source = null;
        const s = (src || '').trim();
        if (!s) throw new Error('无有效的PDF');

        const isDataUrl = s.startsWith('data:application/pdf;base64,');
        const isLikelyBase64 = !/^https?:\/\//i.test(s) && !/^\//.test(s) && !/^blob:/i.test(s) && /^[A-Za-z0-9+/=\s]+$/.test(s);

        if (isDataUrl || isLikelyBase64) {
          source = {
            data: isDataUrl
              ? this.base64ToUint8Array(s.split(',')[1] || '')
              : this.base64ToUint8Array(s)
          };
        } else {
          source = {

            url: s,//pdf文件地址
            disableRange: false,   // 启用 Range 请求
            rangeChunkSize: 256 * 1024,// 分片大小
            disableAutoFetch: false,//禁用自动预取
            disableStream: false,//启用流式加载
            withCredentials: false,//跨域凭证
            disableCreateObjectURL: false,// 使用 Blob URL（默认 false）
            cMapPacked: true, // 使用压缩的 cmap
            onProgress: (progressEvent) => {
              const loaded = progressEvent.loaded || 0;
              const total = progressEvent.total || 0;
              this._loadingLoadedBytes = loaded;
              this._loadingTotalBytes = total;
              if (total > 0 && loaded > 0) {
                const pct = Math.min(Math.round((loaded / total) * 100), 99);
                this._loadingProgress = pct;
                const now = Date.now();
                const elapsed = (now - this._loadingStartTime) / 1000;
                this._loadingSpeedSample.push({ ts: now, bytes: loaded });
                if (this._loadingSpeedSample.length > 10) this._loadingSpeedSample.shift();
                let speedKBps = 0;
                const sample = this._loadingSpeedSample;
                if (sample.length >= 2) {
                  const dt = (sample[sample.length - 1].ts - sample[0].ts) / 1000;
                  if (dt > 0) {
                    speedKBps = Math.round((sample[sample.length - 1].bytes - sample[0].bytes) / dt / 1024);
                  }
                }
                const remain = speedKBps > 0 ? Math.round((total - loaded) / 1024 / speedKBps) : 0;

                const shouldEmit = now - this._lastProgressEmitTs > 300 || pct >= 99;
                if (shouldEmit) {
                  this._lastProgressUpdate = pct;
                  this._lastProgressEmitTs = now;

                  console.log(`[PdfViewer] PDF 拉取: ${pct}%  (${(loaded / 1024 / 1024).toFixed(1)}MB / ${(total / 1024 / 1024).toFixed(1)}MB)  ${speedKBps}KB/s  剩余${remain}s`);
                  this._emitLoadingProgress(pct, total,
                    `正在下载 PDF (${pct}%)  ${speedKBps}KB/s  剩余${remain}s`);
                }

                // 超大/慢速文件自动降 DPI
                if ((total > 100 * 1024 * 1024 || (elapsed > 5 && pct < 80)) && this._dpiMode === 0) {
                  this._dpiMode = 1;
                  this.scale = this._lowDpiScale;
                  console.log('[PdfViewer] 超大文件，启用流畅模式 scale=' + this._lowDpiScale);
                }
              } else if (total === 0 && loaded > 0) {
                this._loadingProgress = -1;
                const now = Date.now();
                const loadedMB = (loaded / 1024 / 1024).toFixed(1);
                if (now - this._lastProgressEmitTs > 300) {
                  this._lastProgressEmitTs = now;
                  this._emitLoadingProgress(-1, 0, `正在下载 PDF（已接收 ${loadedMB}MB）`);
                }
              }
            }
          };
        }

        const loadingTask = pdfjsLib.getDocument(Object.assign({
          verbosity: 0,
          cMapUrl: this.currentCdnHost + 'cmaps/',
          cMapPacked: true
        }, source));
        this._activeLoadingTask = loadingTask;

        // ---- 阶段 2：解析 PDF 结构 ----
        this._fetchPhase = 'parsing';
        this.startPhaseProgress(99, 99, ['正在解析 PDF 结构...', '正在读取页数信息...', '正在准备页面索引...'], 900);
        this.pdfDocument = await loadingTask.promise;
        if (!this.isSessionActive(sessionId) || this._activeLoadingTask !== loadingTask) {
          await this.destroyPdfDocument(this.pdfDocument);
          return;
        }
        this._activeLoadingTask = null;
        this._fetchPhase = 'done';
        this._loadingHint = `解析完成，共 ${this.pdfDocument.numPages} 页`;
        this._loadingProgress = 100;
        this.pageCount = this.pdfDocument.numPages;
        this.adjustPerformanceProfile(this.pageCount);
        this.scrollCurrentPage = 1;
        this.currentPage = 1;
        this._renderedPages = new Set();
        this._renderingPages = new Set();

        this._emitLoadingProgress(100, 0, `解析完成，共 ${this.pageCount} 页`);

        // 获取第一页实际高度
        this._loadingHint = '正在准备第一页...';
        const firstPage = await this.pdfDocument.getPage(1);
        if (!this.isSessionActive(sessionId) || !this.pdfDocument) {
          firstPage.cleanup();
          return;
        }
        const vp = firstPage.getViewport({ scale: this.scale, rotation: this.rotation });
        this.pageHeight = vp.height;
        firstPage.cleanup();

        await this.$nextTick();
        await this.$nextTick();

        // 小文件一次性全量渲染，避免懒加载机制的时序问题
        const totalBytes = this._loadingTotalBytes || 0;
        const isSmallFile = totalBytes > 0 && totalBytes <= 2 * 1024 * 1024;

        if (isSmallFile) {
          this._loadingHint = '正在渲染全部页面...';
          for (let i = 1; i <= this.pageCount; i++) {
            this.renderPage(i);
          }
        } else {
          // 大文件走懒加载
          this._loadingHint = '正在渲染首页...';
          this.renderPage(1);
          if (this.pageCount >= 2) {
            this.renderPage(2);
          }
          this.startIntersectionObserver();
        }

        this.loading = false;
        const elapsed = ((Date.now() - this._loadingStartTime) / 1000).toFixed(1);
        console.log(`[PdfViewer] PDF 加载完成，总页数：${this.pageCount}，耗时：${elapsed}s，scale=${this.scale}`);
        this.$emit('pdf-loaded', {
          totalPages: this.pageCount,
          currentPage: this.currentPage,
          loadTimeSec: parseFloat(elapsed)
        });
      } catch (error) {
        this.stopPhaseProgress();
        this._loadingHint = '';
        console.error('[PdfViewer] PDF加载失败：', error);
        if (error.message && error.message.includes('Failed to fetch')) {
          this.$message.error('PDF加载失败: 跨域访问被阻止，请确保PDF文件支持CORS或使用同域文件');
        }
        this.loading = false;
        this._loadingProgress = -2;
        this._emitLoadingProgress(-2, 0, '加载失败: ' + (error.message || '未知错误'));
        this.$emit('pdf-error', error);
      }
    },

    _emitLoadingProgress(percent, total, hint) {
      this._loadingProgress = percent;
      this.$emit('loading-progress', {
        percent,
        total,
        hint,
        phase: this._fetchPhase
      });
    },

    base64ToUint8Array(base64) {
      const cleaned = base64.replace(/\s+/g, '');
      const binaryString = atob(cleaned);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    },

    resolveDownloadFileName(url) {
      if (this.downloadFileName && String(this.downloadFileName).trim()) {
        return String(this.downloadFileName).trim();
      }
      const fallbackName = 'document.pdf';
      try {
        const s = String(url || '').trim();
        if (!s || s.startsWith('data:application/pdf;base64,')) {
          return fallbackName;
        }
        if (/^[A-Za-z0-9+/=\s]+$/.test(s) && !/^https?:\/\//i.test(s) && !/^\//.test(s) && !/^blob:/i.test(s)) {
          return fallbackName;
        }
        const pathname = new URL(s, window.location.origin).pathname || '';
        const lastSegment = pathname.split('/').filter(Boolean).pop() || fallbackName;
        return /\.pdf$/i.test(lastSegment) ? lastSegment : `${lastSegment}.pdf`;
      } catch (e) {
        return fallbackName;
      }
    },

    triggerDownload(href, fileName) {
      const link = document.createElement('a');
      link.href = href;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    handleVerifySeal() {
      this.$emit('verify-seal');
    },

    async downloadPdf() {
      const s = String(this.pdfUrl || '').trim();
      if (!s) {
        this.$message.error('暂无可下载的PDF');
        return;
      }
      const fileName = this.resolveDownloadFileName(s);
      const isDataUrl = s.startsWith('data:application/pdf;base64,');
      const isLikelyBase64 = !/^https?:\/\//i.test(s) && !/^\//.test(s) && !/^blob:/i.test(s) && /^[A-Za-z0-9+/=\s]+$/.test(s);
      this.downloading = true;
      try {
        if (isDataUrl) {
          this.triggerDownload(s, fileName);
          return;
        }
        if (isLikelyBase64) {
          const bytes = this.base64ToUint8Array(s);
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          this.triggerDownload(blobUrl, fileName);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          return;
        }
        if (s.startsWith('blob:')) {
          this.triggerDownload(s, fileName);
          return;
        }
        if (/^https?:\/\//i.test(s)) {
          window.open(s, '_blank');
          return;
        }
        const response = await fetch(s);
        if (!response.ok) throw new Error('下载失败');
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        this.triggerDownload(blobUrl, fileName);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } catch (error) {
        console.error('PDF下载失败：', error);
        this.$message.error('PDF下载失败');
      } finally {
        this.downloading = false;
      }
    },

    cancelRender(pageNumber) {
      if (this._renderQueue) {
        this._renderQueue = this._renderQueue.filter(x => x !== pageNumber);
      }
      const task = this._renderTasks && this._renderTasks[pageNumber];
      if (task) {
        try {
          task.cancel();
        } catch (_) {}
        delete this._renderTasks[pageNumber];
      }
      if (this._renderingPages) {
        this._renderingPages.delete(pageNumber);
      }
    },

    unrenderPage(pageNumber) {
      this.cancelRender(pageNumber);
      const canvas = this.getCanvasForPage(pageNumber);
      if (canvas) {
        const context = canvas.getContext('2d');
        if (context) {
          context.clearRect(0, 0, canvas.width, canvas.height);
        }
        canvas.width = 0;
        canvas.height = 0;
      }
      if (this._renderedPages) {
        this._renderedPages.delete(pageNumber);
      }
      if (this.pageRenderCompleted) {
        this.$set(this.pageRenderCompleted, pageNumber, false);
      }
      if (this.pageLoadingStates) {
        this.$set(this.pageLoadingStates, pageNumber, false);
      }
    },

    renderPage(pageNumber) {
      if (!this.pdfDocument) return;
      if (!this._renderedPages) this._renderedPages = new Set();
      if (!this._renderingPages) this._renderingPages = new Set();
      if (!this._renderQueue) this._renderQueue = [];

      if (this._renderedPages.has(pageNumber) || this._renderingPages.has(pageNumber)) return;
      if (this._renderQueue.includes(pageNumber)) return;

      if (this.pageLoadingStates) {
        this.$set(this.pageLoadingStates, pageNumber, true);
      }

      this._renderQueue.push(pageNumber);
      this.processRenderQueue();
    },

    renderPageWithRetry(pageNumber, maxRetries = 5, delay = 100) {
      const attemptRender = (retryCount) => {
        const canvas = this.getCanvasForPage(pageNumber);
        if (canvas && canvas.width > 0) return;
        if (this._renderedPages && this._renderedPages.has(pageNumber)) return;
        
        this.renderPage(pageNumber);
        
        if (retryCount < maxRetries) {
          setTimeout(() => attemptRender(retryCount + 1), delay);
        }
      };
      
      this.$nextTick(() => attemptRender(0));
    },

    processRenderQueue() {
      const MAX_CONCURRENT = 2;
      if (!this._activeRenders) this._activeRenders = 0;
      if (!this._renderQueue) this._renderQueue = [];

      while (this._activeRenders < MAX_CONCURRENT && this._renderQueue.length > 0) {
        const pageNumber = this._renderQueue.shift();
        if (this._renderedPages.has(pageNumber) || this._renderingPages.has(pageNumber)) continue;

        this._activeRenders++;
        this._renderPageInternal(pageNumber).finally(() => {
          this._activeRenders--;
          this.processRenderQueue();
        });
      }
    },

    _startFastJump(pageNumber) {
      this._fastJumpMode = true;
      this._fastJumpTarget = pageNumber;
      if (this._fastJumpRestoreTimer) {
        clearTimeout(this._fastJumpRestoreTimer);
      }
      this._fastJumpRestoreTimer = setTimeout(() => {
        this._stopFastJump();
      }, 1200);
    },

    _stopFastJump() {
      if (this._fastJumpRestoreTimer) {
        clearTimeout(this._fastJumpRestoreTimer);
        this._fastJumpRestoreTimer = null;
      }
      this._fastJumpMode = false;
      this._fastJumpTarget = null;
    },

    getRenderScale(pageNumber) {
      if (this._fastJumpMode && this._fastJumpTarget === pageNumber) {
        return Math.max(this.scale * this._fastJumpScaleFactor, this._fastJumpMinScale);
      }
      if (this._dpiMode === 1) {
        return this._lowDpiScale;
      }
      return this.scale;
    },

    shouldCachePage(pageNumber) {
      if (!this._fastJumpMode) return true;
      return Math.abs(pageNumber - (this._fastJumpTarget || pageNumber)) <= 1;
    },

    async _renderPageInternal(pageNumber) {
      const doc = this.pdfDocument;
      const sessionId = this._loadSessionId;
      if (!doc) return;

      if (!this._renderingPages) this._renderingPages = new Set();
      this._renderingPages.add(pageNumber);

      const renderScale = this.getRenderScale(pageNumber);
      const isLowDpiMode = renderScale < this.scale;

      try {
        const page = await doc.getPage(pageNumber);
        if (!this.isSessionActive(sessionId) || this.pdfDocument !== doc) {
          page.cleanup();
          return;
        }
        let canvas = this.getCanvasForPage(pageNumber);

        if (!canvas) {
          for (let retry = 0; retry < 30; retry++) {
            await new Promise(resolve => setTimeout(resolve, 30));
            await this.$nextTick();
            if (!this.isSessionActive(sessionId) || this.pdfDocument !== doc) {
              page.cleanup();
              return;
            }
            canvas = this.getCanvasForPage(pageNumber);
            if (canvas) break;
          }
        }

        if (!canvas) {
          console.warn('[PdfViewer] canvas 未挂载，放弃渲染 page', pageNumber);
          page.cleanup();
          return;
        }

        const context = canvas.getContext('2d');
        const viewport = page.getViewport({
          scale: renderScale,
          rotation: this.rotation
        });

        canvas.height = viewport.height;
        canvas.width = viewport.width;
        this._pageViewportHeights[pageNumber] = viewport.height;

        if (pageNumber === 1) {
          this.pageHeight = viewport.height;
          this._firstPageHeight = viewport.height;
        }

        // ---- 核心优化：优先从缓存 ImageData 绘制 ----
        const cacheKey = `${pageNumber}_${renderScale}_${this.rotation}`;
        const cached = this._pageCache[cacheKey];

        if (cached && cached.width === canvas.width && cached.height === canvas.height) {
          try {
            context.putImageData(cached, 0, 0);
            if (this._renderedPages) this._renderedPages.add(pageNumber);
            this.$set(this.pageRenderCompleted, pageNumber, true);
            this.$set(this.pageLoadingStates, pageNumber, false);
            page.cleanup();
            if (this._renderingPages) this._renderingPages.delete(pageNumber);

            // 低 DPI → 高 DPI 自动升级
            if (isLowDpiMode && this._dpiMode !== 0) {
              this._scheduleDpiUpgrade(pageNumber);
            }
            return;
          } catch (e) {
            delete this._pageCache[cacheKey];
          }
        }

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        const renderTask = page.render(renderContext);
        if (!this._renderTasks) this._renderTasks = {};
        this._renderTasks[pageNumber] = renderTask;

        await renderTask.promise;
        if (!this.isSessionActive(sessionId) || this.pdfDocument !== doc) {
          page.cleanup();
          return;
        }
        delete this._renderTasks[pageNumber];

        // ---- 保存 ImageData 到缓存（限制缓存数量防止爆内存）----
        try {
          if (this.shouldCachePage(pageNumber)) {
            const imgData = context.getImageData(0, 0, canvas.width, canvas.height);
            const keys = Object.keys(this._pageCache);
            if (keys.length >= this._maxCacheSize) {
              delete this._pageCache[keys[0]];
            }
            this._pageCache[cacheKey] = imgData;
          }
        } catch (e) {
          // getImageData 可能因 canvas 跨域污染而抛错，忽略即可
        }

        if (this._renderedPages) {
          this._renderedPages.add(pageNumber);
        }

        this.$emit('page-rendered', {
          pageNumber: pageNumber,
          totalPages: this.pageCount
        });
        page.cleanup();

        // 低 DPI → 高 DPI 自动升级
        if (isLowDpiMode && this._dpiMode !== 0) {
          this._scheduleDpiUpgrade(pageNumber);
        }
      } catch (error) {
        if (error.name !== 'RenderingCancelledException') {
          console.warn('渲染页面出错:', pageNumber, error);
          this.$emit('render-error', error);
        }
      } finally {
        if (this._renderingPages) {
          this._renderingPages.delete(pageNumber);
        }
        if (this.pageLoadingStates) {
          this.$set(this.pageLoadingStates, pageNumber, false);
        }
        if (this.pageRenderCompleted) {
          this.$set(this.pageRenderCompleted, pageNumber, true);
        }
      }
    },


    _scheduleDpiUpgrade(pageNumber) {
      if (this._pendingUpgradePage === pageNumber) return;
      this._pendingUpgradePage = pageNumber;

      if (this._upgradeRafId) cancelAnimationFrame(this._upgradeRafId);
      this._upgradeRafId = requestAnimationFrame(() => {
        this._upgradeRafId = null;
        if (this._pendingUpgradePage !== pageNumber) return;
        this._pendingUpgradePage = null;

        if (this._dpiMode === 1) {
          this._dpiMode = 2;
          this.scale = this._normalDpiScale;
          console.log('[PdfViewer] 升级到标准模式 (scale=' + this._normalDpiScale + ')');
          this.$emit('dpi-mode-changed', { mode: 2, scale: this._normalDpiScale });
          this.$nextTick(() => this.updatePdfDisplay());
        } else if (this._dpiMode === 2) {
          this._dpiMode = 3;
          this.scale = this._highDpiScale;
          console.log('[PdfViewer] 升级到高清模式 (scale=' + this._highDpiScale + ')');
          this.$emit('dpi-mode-changed', { mode: 3, scale: this._highDpiScale });
          this.$nextTick(() => this.updatePdfDisplay());
        }
      });
    },

    renderNearby(centerPage, radius = 2) {
      if (!this.pdfDocument || !this.pageCount) return;
      const start = Math.max(1, centerPage - radius);
      const end = Math.min(this.pageCount, centerPage + radius);
      for (let i = start; i <= end; i++) {
        this.renderPage(i);
      }
    },

    pauseIntersectionObserver() {
      if (this._mutationObserver) {
        this._mutationObserver.disconnect();
        this._mutationObserver = null;
      }
      if (this._observedPages) {
        this._observedPages.clear();
      }
      if (this._pageObserver) {
        this._pageObserver.disconnect();
        this._pageObserver = null;
      }
    },

    resumeIntersectionObserver() {
      if (!this.pdfDocument || this._pageObserver || this._isUserScrollingFast) return;
      this.startIntersectionObserver();
    },

    startIntersectionObserver() {
      if (!this.pdfDocument || this._isUserScrollingFast) return;

      this.pauseIntersectionObserver();
      if (this._pageObserverPollRaf) {
        cancelAnimationFrame(this._pageObserverPollRaf);
        this._pageObserverPollRaf = null;
      }

      const container = this.$refs.pdfContent;
      if (!container) return;

      const tryInit = () => {
        const pages = container.querySelectorAll('.pdf-page');
        if (pages.length === 0) {
          // 还没渲染出来，继续等下一帧
          this._pageObserverPollRaf = requestAnimationFrame(tryInit);
          return;
        }

        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (!entry.isIntersecting) return;
              const pageNum = Number(entry.target.dataset.page);
              if (!pageNum || isNaN(pageNum)) return;
              console.log('[IO] 触发:', pageNum, 'isIntersecting:', entry.isIntersecting, 'intersectionRatio:', entry.intersectionRatio);
              this.currentPage = pageNum;
              this.scrollCurrentPage = pageNum;
              this.renderNearby(pageNum, this.virtualBuffer);
            });
          },
          { root: container, rootMargin: '200px', threshold: 0 }
        );

        this._pageObserver = io;
        pages.forEach(el => io.observe(el));

        // 后续动态追加的页面也需要被 IO 观察
        const mo = new MutationObserver(() => {
          container.querySelectorAll('.pdf-page').forEach(el => {
            if (!this._observedPages) this._observedPages = new Set();
            if (!this._observedPages.has(el)) {
              this._observedPages.add(el);
              io.observe(el);
            }
          });
        });
        mo.observe(container, { childList: true, subtree: false });
        this._mutationObserver = mo;
      };

      this._pageObserverPollRaf = requestAnimationFrame(tryInit);
    },

    async updatePdfDisplay() {
      if (!this.pdfDocument || this.isRenderingAll) return;
      this.isRenderingAll = true;
      try {
        Object.keys(this._renderTasks || {}).forEach(n => this.cancelRender(Number(n)));
        this._renderTasks = {};
        this._renderQueue = [];
        this._activeRenders = 0;

        this._renderedPages = new Set();
        this._renderingPages = new Set();
        this.pageRenderCompleted = {};
        this.pageLoadingStates = {};
        this._pageViewportHeights = {};
        this._firstPageHeight = null;

        const firstPage = await this.pdfDocument.getPage(1);
        const vp = firstPage.getViewport({ scale: this.scale, rotation: this.rotation });
        this.pageHeight = vp.height;
        this._firstPageHeight = vp.height;
        this._pageViewportHeights[1] = vp.height;
        firstPage.cleanup();

        this.scrollCurrentPage = this.currentPage || 1;
        await this.$nextTick();
        this.startIntersectionObserver();
        this.renderNearby(this.scrollCurrentPage, this.virtualBuffer);
      } finally {
        this.isRenderingAll = false;
      }
    },


    async prevPage() {
      if (this.currentPage > 1) {
        if (this.mode === 'iframe') {
          this.sendIframeCommand({ action: 'prevPage' });
        } else {
          this.scrollToPage(this.currentPage - 1);
        }
      }
    },

    async nextPage() {
      if (this.currentPage < this.pageCount) {
        if (this.mode === 'iframe') {
          this.sendIframeCommand({ action: 'nextPage' });
        } else {
          this.scrollToPage(this.currentPage + 1);
        }
      }
    },

    async zoomIn() {
      this.scale = Math.min(this.scale + 0.25, 3.0);
      if (this.mode === 'iframe') {
        this.sendIframeCommand({ action: 'setScale', scale: this.scale });
      } else {
        await this.updatePdfDisplay();
      }
      this.$emit('scale-changed', this.scale);
    },

    async zoomOut() {
      this.scale = Math.max(this.scale - 0.25, 0.2);
      if (this.mode === 'iframe') {
        this.sendIframeCommand({ action: 'setScale', scale: this.scale });
      } else {
        await this.updatePdfDisplay();
      }
      this.$emit('scale-changed', this.scale);
    },

    async fitWidth() {
      this.scale = 1.0;
      await this.updatePdfDisplay();
      this.$emit('scale-changed', this.scale);
    },

    async fitPage() {
      this.scale = 1.0;
      await this.updatePdfDisplay();
      this.$emit('scale-changed', this.scale);
    },

    async goToPage(pageNumber) {
      if (pageNumber >= 1 && pageNumber <= this.pageCount) {
        this.scrollToPage(pageNumber);
      }
    },

    handlePageJump() {
      const pageNumber = Number(this.pageInput);
      if (!Number.isInteger(pageNumber)) {
        this.$message.warning('请输入正确的页码');
        this.pageInput = String(this.currentPage || 1);
        return;
      }
      if (pageNumber < 1 || pageNumber > this.pageCount) {
        this.$message.warning(`请输入 1 到 ${this.pageCount} 之间的页码`);
        this.pageInput = String(this.currentPage || 1);
        return;
      }
      this.goToPage(pageNumber);
    },

    resetPdf() {
      const currentDoc = this.pdfDocument;
      const currentLoadingTask = this._activeLoadingTask;
      this._loadSessionId = Date.now() + Math.random();
      this._activeLoadingTask = null;
      this.pauseIntersectionObserver();
      this.loading = false;
      this.pdfDocument = null;
      this.currentPage = 1;
      this.pageCount = 0;
      this.pageInput = '1';
      this.scale = this.initialScale;
      this.virtualBuffer = this._baseVirtualBuffer;
      this.jumpRenderBuffer = this._baseJumpRenderBuffer;
      this._maxCacheSize = this._baseMaxCacheSize;
      this.scrollCurrentPage = 1;
      this._loadingProgress = 0;
      this._loadingTotalBytes = 0;
      this._loadingLoadedBytes = 0;
      this._loadingHint = '';
      this.stopPhaseProgress();
      this._pageCache = {};
      this._pendingUpgradePage = null;
      this._stopFastJump();
      if (this._upgradeRafId) {
        cancelAnimationFrame(this._upgradeRafId);
        this._upgradeRafId = null;
      }
      Object.keys(this._renderTasks || {}).forEach(n => this.cancelRender(Number(n)));
      this._renderTasks = {};
      this._renderQueue = [];
      this._activeRenders = 0;
      this._renderedPages = new Set();
      this._renderingPages = new Set();
      this.pageRenderCompleted = {};
      this.pageLoadingStates = {};
      this._pageViewportHeights = {};
      this._firstPageHeight = null;
      this.clearAllCanvases();
      if (currentLoadingTask && typeof currentLoadingTask.destroy === 'function') {
        currentLoadingTask.destroy().catch(() => {});
      }
      this.destroyPdfDocument(currentDoc);
    },

    toggleRotation() {
      this.rotation = (this.rotation + 90) % 360;
      if (this.mode === 'iframe') {
        this.sendIframeCommand({ action: 'setRotation', degrees: this.rotation });
      } else if (this.pdfDocument) {
        this.updatePdfDisplay();
      }
      this.$emit('rotation-changed', this.rotation);
    },

    rotateCw() {
      this.rotation = (this.rotation + 90) % 360;
      if (this.mode === 'iframe') {
        this.sendIframeCommand({ action: 'setRotation', degrees: this.rotation });
      } else if (this.pdfDocument) {
        this.updatePdfDisplay();
      }
      this.$emit('rotation-changed', this.rotation);
    },

    rotateCcw() {
      this.rotation = (this.rotation - 90 + 360) % 360;
      if (this.mode === 'iframe') {
        this.sendIframeCommand({ action: 'setRotation', degrees: this.rotation });
      } else if (this.pdfDocument) {
        this.updatePdfDisplay();
      }
      this.$emit('rotation-changed', this.rotation);
    },

    firstPage() {
      if (this.mode === 'iframe') {
        this.sendIframeCommand({ action: 'firstPage' });
      } else {
        this.goToPage(1);
      }
    },

    lastPage() {
      if (this.mode === 'iframe') {
        this.sendIframeCommand({ action: 'lastPage' });
      } else {
        this.goToPage(this.pageCount);
      }
    },

    openDocumentProperties() {
      if (this.mode === 'iframe') {
        this.sendIframeCommand({ action: 'documentProperties' });
      }
    },

    getPdfInfo() {
      return {
        currentPage: this.currentPage,
        totalPages: this.pageCount,
        scale: this.scale,
        rotation: this.rotation,
        loading: this.loading,
        hasPdf: !!this.pdfDocument
      };
    },

    pageStyle(pageNumber) {
      return {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        marginBottom: (this.pageGap || 12) + 'px'
      };
    },

    containerStyle(pageNumber) {
      const h = this._pageViewportHeights[pageNumber] || this._firstPageHeight || 1100;
      const w = Math.round(h / 1.4142);
      return {
        width: w ? (w + 'px') : '800px',
        height: h + 'px',
        position: 'relative'
      };
    },

    scrollToPage(pageNumber) {
      if (!pageNumber || pageNumber < 1 || pageNumber > this.pageCount) return;

      const prevPage = this.currentPage || 1;
      const jumpDistance = Math.abs(pageNumber - prevPage);
      const isLongJump = jumpDistance > this._fastJumpDistanceThreshold;

      if (isLongJump) {
        this._startFastJump(pageNumber);
      } else {
        this._stopFastJump();
      }

      this.currentPage = pageNumber;
      this.scrollCurrentPage = pageNumber;

      this.$nextTick(() => {
        const container = this.$refs.pdfContent;
        const targetOffset = (pageNumber - 1) * (this.pageHeight + this.pageGap);

        if (container) {
          if (isLongJump) {
            container.scrollTop = targetOffset;
          } else {
            const targetPageEl = this.$el.querySelector(`.pdf-page[data-page="${pageNumber}"]`);
            if (targetPageEl) {
              targetPageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
              container.scrollTop = targetOffset;
            }
          }
        }

        const renderRadius = isLongJump ? this.jumpRenderBuffer : this.virtualBuffer;
        this.renderPage(pageNumber);
        this.renderNearby(pageNumber, renderRadius);
        this.handleScroll();

        if (isLongJump) {
          const settlePage = pageNumber;
          setTimeout(() => {
            if (this.currentPage === settlePage) {
              this._stopFastJump();
              this.renderNearby(settlePage, this.virtualBuffer);
            }
          }, 260);
        }
      });
    },

    // 使用 RAF 优化滚动性能，防止重复触发
    handleScroll() {
      // RAF 调度要放在最前面，确保每次滚动都能触发 _doHandleScroll
      if (this._scrollTimerRafId) {
        cancelAnimationFrame(this._scrollTimerRafId);
      }
      this._scrollTimerRafId = requestAnimationFrame(() => {
        this._scrollTimerRafId = null;
        this._doHandleScroll(false);
      });

      if (!this._isUserScrollingFast) {
        this.pauseIntersectionObserver();
      }
      this._isUserScrollingFast = true;
      if (this._scrollSettleTimer) {
        clearTimeout(this._scrollSettleTimer);
      }
      this._scrollSettleTimer = setTimeout(() => {
        this._isUserScrollingFast = false;
        this._scrollSettleTimer = null;
        this._doHandleScroll(true);
        this.resumeIntersectionObserver();
      }, this._scrollSettleDelay);
    },

    _doHandleScroll(shouldRenderNearby = false) {
      const container = this.$refs.pdfContent;
      if (!container || !this.pageCount) return;

      const scrollTop = container.scrollTop;
      const pageFullHeight = this.pageHeight + this.pageGap;
      let newPage = Math.floor(scrollTop / pageFullHeight) + 1;
      newPage = Math.max(1, Math.min(newPage, this.pageCount));

      console.log('[_doHandleScroll] scrollTop:', scrollTop, 'pageHeight:', this.pageHeight, 'pageGap:', this.pageGap, 'pageFullHeight:', pageFullHeight, '-> newPage:', newPage, 'shouldRenderNearby:', shouldRenderNearby, '_isUserScrollingFast:', this._isUserScrollingFast);

      const prevCurrent = this.currentPage;
      this.currentPage = newPage;
      this.scrollCurrentPage = newPage;

      if (shouldRenderNearby || !this._isUserScrollingFast) {
        const renderRadius = this._fastJumpMode ? this.jumpRenderBuffer : this.virtualBuffer;
        this.renderPage(newPage);
        this.renderNearby(newPage, renderRadius);
      }

      if (newPage !== prevCurrent) {
        this.$emit('page-changed', { currentPage: newPage, totalPages: this.pageCount });
      }
    }
  },
  beforeDestroy() {
    this.stopPhaseProgress();
    this._iframeMessageHandler && window.removeEventListener('message', this._iframeMessageHandler);
    if (this._scrollRafId) {
      cancelAnimationFrame(this._scrollRafId);
      this._scrollRafId = null;
    }
    if (this._scrollTimerRafId) {
      cancelAnimationFrame(this._scrollTimerRafId);
      this._scrollTimerRafId = null;
    }
    if (this._scrollSettleTimer) {
      clearTimeout(this._scrollSettleTimer);
      this._scrollSettleTimer = null;
    }
    if (this._pageObserverPollRaf) {
      cancelAnimationFrame(this._pageObserverPollRaf);
      this._pageObserverPollRaf = null;
    }
    if (this._pageObserver) {
      this._pageObserver.disconnect();
      this._pageObserver = null;
    }
    if (this._upgradeRafId) {
      cancelAnimationFrame(this._upgradeRafId);
      this._upgradeRafId = null;
    }
    this._stopFastJump();
    if (this.pdfDocument) {
      this.pdfDocument.destroy();
    }
    const container = this.$refs.pdfContent;
    if (container) {
      container.removeEventListener('scroll', this.handleScroll);
    }
    if (Array.isArray(this._scrollTargets)) {
      this._scrollTargets.forEach(t => {
        try { t.removeEventListener('scroll', this.handleScroll); } catch (e) { }
      });
      this._scrollTargets = [];
    }
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
    if (this._iframeMessageHandler) {
      window.removeEventListener('message', this._iframeMessageHandler);
      this._iframeMessageHandler = null;
    }
    if (this._io) {
      try { this._io.disconnect(); } catch (e) { }
      this._io = null;
    }
  }
}
</script>

<style scoped>
.pdf-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-toolbar {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%);
  border-bottom: 1px solid #e4e7ed;
  flex-shrink: 0;
  gap: 8px;
  flex-wrap: wrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
}

.toolbar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 36px;
  height: 32px;
  padding: 0 12px;
  font-size: 12px;
  color: #606266;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  user-select: none;
  font-weight: 500;
}

.toolbar-btn .btn-icon {
  font-size: 14px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.toolbar-btn:hover:not(:disabled) {
  color: #409eff;
  border-color: #409eff;
  background: #ecf5ff;
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.18);
  transform: translateY(-1px);
}

.toolbar-btn:active:not(:disabled) {
  transform: translateY(0);
  background: #d9ecff;
  box-shadow: 0 1px 2px rgba(64, 158, 255, 0.1);
}

.toolbar-btn:focus-visible {
  outline: 2px solid #409eff;
  outline-offset: 1px;
}

.toolbar-btn:disabled {
  color: #c0c4cc;
  cursor: not-allowed;
  opacity: 0.55;
  background: #f5f7fa;
  border-color: #e4e7ed;
  box-shadow: none;
  transform: none;
}

.toolbar-btn.is-loading {
  color: #909399;
}

.verify-seal-btn {
  color: #fff;
  background: linear-gradient(135deg, #409eff 0%, #3a8ee6 100%);
  border-color: #409eff;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.25);
}

.verify-seal-btn:hover:not(:disabled) {
  color: #fff;
  background: linear-gradient(135deg, #66b1ff 0%, #409eff 100%);
  border-color: #66b1ff;
  box-shadow: 0 4px 10px rgba(64, 158, 255, 0.35);
  transform: translateY(-1px);
}

.verify-seal-btn:active:not(:disabled) {
  background: linear-gradient(135deg, #3a8ee6 0%, #2c7fd1 100%);
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(64, 158, 255, 0.2);
}

.verify-seal-btn:disabled {
  color: #fff;
  background: #a0cfff;
  border-color: #a0cfff;
  box-shadow: none;
  transform: none;
  opacity: 0.6;
}

.page-input {
  width: 72px;
  height: 32px;
  padding: 0 8px;
  font-size: 13px;
  color: #606266;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  outline: none;
  text-align: center;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-weight: 500;
}

.page-input:hover {
  border-color: #c0c4cc;
}

.page-input:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.page-input:focus {
  border-color: #409eff;
}

.loading-progress-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #409eff;
  white-space: nowrap;
}

.progress-text {
  min-width: 70px;
  font-weight: 500;
}

.progress-track {
  width: 120px;
  height: 6px;
  background: #e4e7ed;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}

.progress-track.is-indeterminate {
  background: #eef4ff;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #66b1ff, #409eff);
  border-radius: 3px;
  transition: width 0.3s ease;
  max-width: 100%;
}

.progress-fill.is-indeterminate {
  position: absolute;
  top: 0;
  left: 0;
  width: 36px;
  max-width: none;
  animation: progress-indeterminate 1.1s ease-in-out infinite;
}

@keyframes progress-indeterminate {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(420%);
  }
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.page-info,
.scale-info,
.rotation-info {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  font-size: 13px;
  color: #606266;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  min-width: 60px;
  text-align: center;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}

.pdf-content {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
  background: #f9f9f9;
}

.pdf-iframe-container {
  width: 100%;
  height: 100%;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

.pdf-canvas-container {
  width: 100%;
  height: 100%;
  overflow: auto;
}

.pdf-pages {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
}

.pdf-page {
  display: flex;
  justify-content: center;
}

.pdf-loading {
  text-align: center;
  color: #666;
}

.pdf-loading i {
  font-size: 24px;
  margin-bottom: 10px;
}

.pdf-placeholder {
  text-align: center;
  color: #999;
}

.pdf-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
}

.pdf-document {
  max-width: 100%;
  background: white;
  display: block;
}

.pdf-page-container {
  position: relative;
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

.pdf-page-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.94);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
}

.mask-content {
  text-align: center;
  color: #409EFF;
}

.mask-content i {
  font-size: 32px;
  margin-bottom: 10px;
}

.mask-content p {
  font-size: 13px;
  margin: 0;
  color: #8c939d;
  font-weight: 500;
}

@media (max-width: 768px) {
  .pdf-toolbar {
    justify-content: center;
  }
}
</style>
